import { Button, ICON_COLOR } from '@zep-us/zus';
import { ChartLineIcon, PlayIcon, RectanglePortraitStackFillIcon, SquareStackIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { NewChip } from '@/shared/components/Chip/NewChip';
import { CTAButtonRightSlot } from './CTAButtonRightSlot';
import { WorksheetGradientIcon } from './WorksheetGradientIcon';

/**
 * ViewerCTAButtons Props
 */
export interface ViewerCTAButtonsProps {
  /** 복사 버튼 클릭 핸들러 */
  onDuplicate: () => void;
  /** 입장 버튼 클릭 핸들러 */
  onEnter: () => void;
  /** 워크시트 내보내기 핸들러 */
  onExportWorksheet: () => void;
  /** 학습 리포트 핸들러(관리자 전용) */
  onGoToLms?: () => void;
  /** 플래시카드 학습 진입 핸들러 */
  onFlashcard: () => void;
}

/**
 * ViewerCTAButtons - 뷰어용 2개 버튼 레이아웃
 *
 * @description
 * 다른 사용자의 퀴즈를 보는 뷰어에게 표시되는 CTA 버튼 그룹입니다.
 * - Primary CTA: 내 퀴즈로 복사
 * - Secondary CTA: 퀴즈 입장하기
 *
 * 레이아웃:
 * - Mobile: 세로 스택 (100% width)
 * - Desktop: 가로 배치 (복사 버튼 좌측 정렬, 입장 버튼 우측 정렬)
 *
 * @example
 * ```tsx
 * <ViewerCTAButtons
 *   onDuplicate={handleDuplicate}
 *   onEnter={handleEnter}
 * />
 * ```
 */
export const ViewerCTAButtons = ({
  onDuplicate,
  onEnter,
  onGoToLms,
  onExportWorksheet,
  onFlashcard
}: ViewerCTAButtonsProps) => {
  const {
    t
  } = useTranslation(['common']);
  const buttonsStyle = 'group w-full justify-start px-md gap-xs';
  return <div className="flex w-full flex-col gap-xs" data-sentry-component="ViewerCTAButtons" data-sentry-source-file="ViewerCTAButtons.tsx">
      {/* Primary CTA: 내 퀴즈로 복사 */}
      <Button size="md" intent="gradient" variant="contained" leftSlot={<SquareStackIcon size="sm" fill={ICON_COLOR.inverse} />} rightSlot={<CTAButtonRightSlot />} onClick={onDuplicate} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="ViewerCTAButtons.tsx">
        {t('detail.copyToMyQuiz')}
      </Button>
      <div className="flex flex-col gap-xs pt-sm">
        <p className="text-caption-lg-semibold text-alternative">
          {t('common:detail.quizSolve')}
        </p>
        {/* Secondary CTA: 퀴즈 체험하기 */}
        <Button size="md" variant="outlined" intent="alternative" onClick={onEnter} leftSlot={<PlayIcon size="sm" fill={ICON_COLOR.strong} />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="ViewerCTAButtons.tsx">
          {t('detail.cta.tutorial')}
        </Button>
        {/* 2. 플래시카드 - Flashcard button */}
        <Button size="md" variant="outlined" intent="neutral" onClick={onFlashcard} leftSlot={<RectanglePortraitStackFillIcon size="sm" className="text-blue-300" />} rightSlot={<CTAButtonRightSlot badge={<NewChip className="bg-blue-50 text-blue-500" />} />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="ViewerCTAButtons.tsx">
          {t('common:flashcard.label')}
        </Button>
        {/* 3. 워크시트 - Worksheet button */}
        <Button size="md" variant="outlined" intent="neutral" onClick={onExportWorksheet} leftSlot={<WorksheetGradientIcon size="sm" />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle} data-sentry-element="Button" data-sentry-source-file="ViewerCTAButtons.tsx">
          {t('common:quizWorksheet.label')}
        </Button>
        {/* 3. 학습 리포트 - Alternative button (관리자 전용) */}
        {onGoToLms && <Button size="md" variant="contained" intent="alternative" onClick={onGoToLms} leftSlot={<ChartLineIcon size="sm" fill={ICON_COLOR.strong} />} rightSlot={<CTAButtonRightSlot />} className={buttonsStyle}>
            {t('detail.learningReport')}
          </Button>}
      </div>
    </div>;
};